import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Tag, Spin, Alert, Divider } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const BASE_URL = process.env.REACT_APP_API_URL || '';

function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const matches = [
      boldMatch && { type: 'bold', match: boldMatch },
      codeMatch && { type: 'code', match: codeMatch },
      linkMatch && { type: 'link', match: linkMatch },
    ].filter(Boolean).sort((a, b) => a.match.index - b.match.index);
    if (matches.length === 0) { parts.push(<span key={key++}>{remaining}</span>); break; }
    const first = matches[0];
    if (first.match.index > 0) parts.push(<span key={key++}>{remaining.slice(0, first.match.index)}</span>);
    if (first.type === 'bold') parts.push(<strong key={key++}>{first.match[1]}</strong>);
    else if (first.type === 'code') parts.push(
      <code key={key++} style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: 3, fontSize: 13, fontFamily: 'monospace', color: '#c7254e' }}>
        {first.match[1]}
      </code>
    );
    else if (first.type === 'link') parts.push(<a key={key++} href={first.match[2]} target="_blank" rel="noreferrer">{first.match[1]}</a>);
    remaining = remaining.slice(first.match.index + first.match[0].length);
  }
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function SpecMarkdown({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let inCode = false, codeLines = [], codeLang = '';
  lines.forEach((line, i) => {
    if (line.startsWith('```')) {
      if (!inCode) { inCode = true; codeLang = line.slice(3).trim(); codeLines = []; }
      else {
        elements.push(
          <pre key={i} style={{ background: '#1e1e1e', color: '#d4d4d4', borderRadius: 6, padding: '12px 16px', fontSize: 13, overflowX: 'auto', marginBottom: 12, lineHeight: 1.6 }}>
            {codeLang && <div style={{ color: '#858585', fontSize: 11, marginBottom: 6 }}>{codeLang}</div>}
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        inCode = false; codeLines = []; codeLang = '';
      }
      return;
    }
    if (inCode) { codeLines.push(line); return; }
    if (line.startsWith('# ')) elements.push(<Title key={i} level={2} style={{ marginTop: 28, marginBottom: 8, color: '#1a1a2e' }}>{line.slice(2)}</Title>);
    else if (line.startsWith('## ')) elements.push(<Title key={i} level={3} style={{ marginTop: 24, marginBottom: 6, color: '#003a8c', borderBottom: '2px solid #e8f4ff', paddingBottom: 4 }}>{line.slice(3)}</Title>);
    else if (line.startsWith('### ')) elements.push(<Title key={i} level={4} style={{ marginTop: 18, marginBottom: 4, color: '#0050b3' }}>{line.slice(4)}</Title>);
    else if (line.startsWith('#### ')) elements.push(<Title key={i} level={5} style={{ marginTop: 14, marginBottom: 4 }}>{line.slice(5)}</Title>);
    else if (line.startsWith('- ') || line.startsWith('* ')) elements.push(<div key={i} style={{ paddingLeft: 20, marginBottom: 4, lineHeight: 1.7 }}>• {renderInline(line.slice(2))}</div>);
    else if (/^\d+\. /.test(line)) {
      const m = line.match(/^(\d+)\. (.*)/);
      if (m) elements.push(<div key={i} style={{ paddingLeft: 20, marginBottom: 4, lineHeight: 1.7 }}>{m[1]}. {renderInline(m[2])}</div>);
    } else if (line.startsWith('> ')) elements.push(
      <blockquote key={i} style={{ borderLeft: '4px solid #1890ff', paddingLeft: 14, margin: '8px 0', color: '#595959', fontStyle: 'italic' }}>
        {renderInline(line.slice(2))}
      </blockquote>
    );
    else if (line.startsWith('---')) elements.push(<Divider key={i} />);
    else if (line.trim() === '') elements.push(<div key={i} style={{ height: 8 }} />);
    else elements.push(<Paragraph key={i} style={{ marginBottom: 6, lineHeight: 1.8 }}>{renderInline(line)}</Paragraph>);
  });
  return <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{elements}</div>;
}

export default function DevSpecViewPage() {
  const { token } = useParams();
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${BASE_URL}/api/v1/dev-specs/public/${token}`)
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.detail || '找不到此規格')))
      .then(data => { setSpec(data); setLoading(false); })
      .catch(err => { setError(typeof err === 'string' ? err : '載入失敗'); setLoading(false); });
  }, [token]);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7ff' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0d3b8c 100%)', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <FileTextOutlined style={{ color: '#fff', fontSize: 22 }} />
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 18, letterSpacing: 1 }}>開發規格文件</span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginLeft: 8 }}>marcom AI 行銷平台</span>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 60px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#595959' }}>載入規格中...</div>
          </div>
        )}
        {error && (
          <Alert type="error" message={error} description="連結可能已失效或已被刪除。" showIcon style={{ marginTop: 40 }} />
        )}
        {spec && (
          <>
            {/* Spec header */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <Title level={2} style={{ margin: 0, color: '#1a1a2e' }}>{spec.title}</Title>
                <Tag color="blue" style={{ fontSize: 13, padding: '2px 10px' }}>v{spec.version_num}</Tag>
              </div>
              {spec.note && <Text type="secondary" style={{ fontSize: 14 }}>{spec.note}</Text>}
              <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(spec.selected_features || []).map(key => (
                  <Tag key={key} style={{ fontSize: 12 }}>{key}</Tag>
                ))}
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
                {spec.creator_name && <span>by {spec.creator_name} · </span>}
                {spec.created_at && new Date(spec.created_at).toLocaleString('zh-TW')}
              </div>
            </div>

            {/* Spec content */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <SpecMarkdown content={spec.spec_content} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
